import {
  addDoc,
  collection,
  doc,
  onSnapshot,
  query,
  Timestamp,
  updateDoc,
  where,
} from 'firebase/firestore'
import { useEffect, useMemo, useState } from 'react'
import { auth, db } from '../../firebase'
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Event as EventIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
} from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { arSA } from 'date-fns/locale'
import {
  formatDateAr,
  getDaysRemaining,
  getMerchantStatus,
  paginate,
  type MerchantDoc,
  type MerchantStatus,
} from './subscriptionHelpers'

type FilterType = 'all' | MerchantStatus

const FILTER_LABELS: Record<FilterType, string> = {
  all: 'الكل',
  active: 'نشط',
  unlimited: 'غير محدود',
  expiring: 'ينتهي قريباً',
  inactive: 'غير نشط',
}

export default function MerchantSubscriptionsPage() {
  const [merchants, setMerchants] = useState<MerchantDoc[]>([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filter, setFilter] = useState<FilterType>('all')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(25)
  const [selectedIds, setSelectedIds] = useState<string[]>([])
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info' | 'warning',
  })
  const [manualCheckLoading, setManualCheckLoading] = useState(false)
  const [subscriptionDialog, setSubscriptionDialog] = useState(false)
  const [bulkMode, setBulkMode] = useState(false)
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantDoc | null>(null)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  useEffect(() => {
    const q = query(collection(db, 'users'), where('type', '==', 'seller'))
    const unsub = onSnapshot(
      q,
      (snap) => {
        const rows: MerchantDoc[] = snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) }))
        rows.sort((a, b) => {
          const dateA = a.createdAt?.toDate?.() || new Date(0)
          const dateB = b.createdAt?.toDate?.() || new Date(0)
          return dateB.getTime() - dateA.getTime()
        })
        setMerchants(rows)
      },
      () => setSnackbar({ open: true, message: 'حدث خطأ في تحميل التجار', severity: 'error' }),
    )
    return () => unsub()
  }, [])

  const stats = useMemo(() => {
    const next = { total: merchants.length, active: 0, unlimited: 0, expiring: 0, inactive: 0 }
    merchants.forEach((merchant) => {
      next[getMerchantStatus(merchant)] += 1
    })
    return next
  }, [merchants])

  const filteredMerchants = useMemo(() => {
    const queryText = searchQuery.trim().toLowerCase()
    return merchants.filter((merchant) => {
      const status = getMerchantStatus(merchant)
      if (filter !== 'all' && status !== filter) return false
      if (!queryText) return true
      return (
        merchant.name?.toLowerCase().includes(queryText) ||
        merchant.phone?.includes(queryText) ||
        false
      )
    })
  }, [merchants, searchQuery, filter])

  useEffect(() => {
    setPage(0)
    setSelectedIds([])
  }, [searchQuery, filter])

  const pageRows = paginate(filteredMerchants, page, rowsPerPage)
  const selectedOnPage = pageRows.filter((row) => selectedIds.includes(row.id))

  const notify = (message: string, severity: typeof snackbar.severity) =>
    setSnackbar({ open: true, message, severity })

  const sendNotification = async (
    userId: string,
    title: string,
    body: string,
    type: string,
    data: Record<string, unknown> = {},
  ) => {
    await addDoc(collection(db, 'notifications'), {
      userId,
      type,
      title,
      body,
      data,
      isRead: false,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    })
  }

  const deactivateMerchant = async (merchantId: string, merchantName: string) => {
    await updateDoc(doc(db, 'users', merchantId), {
      status: 'inactive',
      isActive: false,
      deactivatedBy: 'subscription',
      deactivatedAt: Timestamp.now(),
      deactivatedReason: 'انتهاء صلاحية الاشتراك — يرجى الدفع من التطبيق لتجديد الاشتراك',
      updatedAt: Timestamp.now(),
    })
    await addDoc(collection(db, 'admin_activity_logs'), {
      action: 'auto_deactivate_subscription',
      targetUserId: merchantId,
      targetUserName: merchantName,
      details: 'تم تعطيل الحساب تلقائياً بسبب انتهاء الاشتراك',
      timestamp: Timestamp.now(),
    })
    await sendNotification(
      merchantId,
      'انتهى اشتراكك',
      'لا يمكنك إرسال عروض حتى تدفع الاشتراك من التطبيق.',
      'subscription_expired',
      { action: 'pay_subscription' },
    )
  }

  const reactivateMerchant = async (merchantId: string, merchantName: string) => {
    await updateDoc(doc(db, 'users', merchantId), {
      status: 'active',
      isActive: true,
      deactivatedBy: null,
      deactivatedAt: null,
      deactivatedReason: null,
      updatedAt: Timestamp.now(),
    })
    await addDoc(collection(db, 'admin_activity_logs'), {
      action: 'reactivate_subscription',
      targetUserId: merchantId,
      targetUserName: merchantName,
      details: 'تم إعادة تنشيط الحساب بسبب تجديد الاشتراك',
      timestamp: Timestamp.now(),
    })
    await sendNotification(
      merchantId,
      'تم تفعيل حسابك',
      'عزيزي التاجر، تم إعادة تفعيل حسابك بنجاح. يمكنك الآن استخدام التطبيق بشكل طبيعي.',
      'subscription_renewed',
      { action: 'account_reactivated' },
    )
  }

  const sendSubscriptionReminder = async (
    merchantId: string,
    merchantName: string,
    daysRemaining: number,
  ) => {
    await sendNotification(
      merchantId,
      'تذكير بموعد الإشتراك',
      'شريك KARSAL العزيز ، لضمان استمرار عمل التطبيق يرجى سرعة سداد رسوم الاشتراك خلال يوم',
      'subscription_reminder',
      { daysRemaining, action: 'subscription_reminder' },
    )
    await addDoc(collection(db, 'admin_activity_logs'), {
      action: 'subscription_reminder_sent',
      targetUserId: merchantId,
      targetUserName: merchantName,
      details: `تم إرسال تذكير بانتهاء الاشتراك (متبقي ${daysRemaining} يوم)`,
      timestamp: Timestamp.now(),
    })
  }

  const applySubscriptionDate = async (merchant: MerchantDoc, date: Date) => {
    const now = new Date()
    now.setHours(0, 0, 0, 0)
    const endDate = new Date(date)
    endDate.setHours(0, 0, 0, 0)
    const daysUntilExpiry = Math.ceil((endDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))
    const merchantName = merchant.name || 'غير محدد'
    const status = getMerchantStatus(merchant)
    const isCurrentlyActive = status !== 'inactive'

    await updateDoc(doc(db, 'users', merchant.id), {
      subscriptionEndDate: Timestamp.fromDate(date),
      subscriptionReminderSentFor: null,
      updatedAt: Timestamp.now(),
    })
    await addDoc(collection(db, 'admin_activity_logs'), {
      action: 'update_subscription',
      targetUserId: merchant.id,
      targetUserName: merchantName,
      details: `تم تحديد تاريخ انتهاء الاشتراك: ${date.toLocaleDateString('ar-EG')} (${daysUntilExpiry} يوم متبقي)`,
      timestamp: Timestamp.now(),
    })

    if (daysUntilExpiry <= 0 && isCurrentlyActive) {
      await deactivateMerchant(merchant.id, merchantName)
    } else if (daysUntilExpiry === 1 && isCurrentlyActive) {
      await sendSubscriptionReminder(merchant.id, merchantName, daysUntilExpiry)
    } else if (daysUntilExpiry > 0 && !isCurrentlyActive) {
      await reactivateMerchant(merchant.id, merchantName)
    }
  }

  const applyUnlimited = async (merchant: MerchantDoc) => {
    const needsReactivation = getMerchantStatus(merchant) === 'inactive'
    await updateDoc(doc(db, 'users', merchant.id), {
      subscriptionEndDate: null,
      updatedAt: Timestamp.now(),
    })
    if (needsReactivation) {
      await reactivateMerchant(merchant.id, merchant.name || 'غير محدد')
    }
    await addDoc(collection(db, 'admin_activity_logs'), {
      action: needsReactivation ? 'remove_subscription_and_reactivate' : 'remove_subscription',
      targetUserId: merchant.id,
      targetUserName: merchant.name,
      details: 'تم إزالة تاريخ انتهاء الاشتراك (اشتراك غير محدود)',
      timestamp: Timestamp.now(),
    })
  }

  const openSubscriptionDialog = (merchant: MerchantDoc) => {
    setBulkMode(false)
    setSelectedMerchant(merchant)
    if (merchant.subscriptionEndDate) {
      const date = merchant.subscriptionEndDate.toDate
        ? merchant.subscriptionEndDate.toDate()
        : new Date(merchant.subscriptionEndDate)
      setSelectedDate(date)
    } else {
      const defaultDate = new Date()
      defaultDate.setDate(defaultDate.getDate() + 30)
      setSelectedDate(defaultDate)
    }
    setSubscriptionDialog(true)
  }

  const openBulkDateDialog = () => {
    setBulkMode(true)
    setSelectedMerchant(null)
    const defaultDate = new Date()
    defaultDate.setDate(defaultDate.getDate() + 30)
    setSelectedDate(defaultDate)
    setSubscriptionDialog(true)
  }

  const saveSubscription = async () => {
    if (!selectedDate) return
    try {
      if (bulkMode) {
        const targets = merchants.filter((m) => selectedIds.includes(m.id))
        for (const merchant of targets) {
          await applySubscriptionDate(merchant, selectedDate)
        }
        notify(`تم تحديث تاريخ الاشتراك لـ ${targets.length} تاجر`, 'success')
        setSelectedIds([])
      } else if (selectedMerchant) {
        await applySubscriptionDate(selectedMerchant, selectedDate)
        notify('تم تحديث تاريخ الاشتراك بنجاح', 'success')
      }
      setSubscriptionDialog(false)
    } catch {
      notify('حدث خطأ أثناء تحديث الاشتراك', 'error')
    }
  }

  const removeSubscription = async () => {
    try {
      if (bulkMode) {
        const targets = merchants.filter((m) => selectedIds.includes(m.id))
        for (const merchant of targets) {
          await applyUnlimited(merchant)
        }
        notify(`تم جعل ${targets.length} تاجر غير محدود`, 'success')
        setSelectedIds([])
      } else if (selectedMerchant) {
        await applyUnlimited(selectedMerchant)
        notify('تم إزالة تاريخ الاشتراك (اشتراك غير محدود)', 'success')
      }
      setSubscriptionDialog(false)
    } catch {
      notify('حدث خطأ أثناء إزالة الاشتراك', 'error')
    }
  }

  const runManualSubscriptionCheck = async () => {
    const user = auth.currentUser
    if (!user) {
      notify('يرجى تسجيل الدخول أولاً', 'error')
      return
    }
    setManualCheckLoading(true)
    try {
      const token = await user.getIdToken()
      const projectId = (db as any).app?.options?.projectId || 'welamart-cef13'
      const url = `https://us-central1-${projectId}.cloudfunctions.net/manualSubscriptionCheck`
      const res = await fetch(url, { method: 'GET', headers: { Authorization: `Bearer ${token}` } })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        notify(data?.error || 'فشل فحص الاشتراكات', 'error')
        return
      }
      notify(
        `تم الفحص: ${data.deactivationsCount ?? 0} تعطيل، ${data.remindersCount ?? 0} تذكير.`,
        'success',
      )
    } catch (e: any) {
      notify(e?.message || 'حدث خطأ أثناء فحص الاشتراكات', 'error')
    } finally {
      setManualCheckLoading(false)
    }
  }

  const toggleAllPage = (checked: boolean) => {
    if (checked) {
      setSelectedIds(Array.from(new Set([...selectedIds, ...pageRows.map((row) => row.id)])))
    } else {
      const pageIds = new Set(pageRows.map((row) => row.id))
      setSelectedIds(selectedIds.filter((id) => !pageIds.has(id)))
    }
  }

  const statusChip = (merchant: MerchantDoc) => {
    const status = getMerchantStatus(merchant)
    const days = getDaysRemaining(merchant.subscriptionEndDate)
    if (status === 'inactive') return <Chip size="small" color="error" label="غير نشط" />
    if (status === 'expiring')
      return <Chip size="small" color="warning" label={`ينتهي خلال ${days} يوم`} />
    if (status === 'unlimited')
      return <Chip size="small" color="success" icon={<CheckCircleIcon />} label="غير محدود" />
    return <Chip size="small" color="success" label={`${days} يوم`} />
  }

  const statCards: Array<{ key: FilterType; label: string; value: number; color: string }> = [
    { key: 'all', label: 'كل التجار', value: stats.total, color: '#0f766e' },
    { key: 'active', label: 'نشط', value: stats.active, color: '#059669' },
    { key: 'unlimited', label: 'غير محدود', value: stats.unlimited, color: '#0284c7' },
    { key: 'expiring', label: 'ينتهي قريباً', value: stats.expiring, color: '#d97706' },
    { key: 'inactive', label: 'غير نشط', value: stats.inactive, color: '#dc2626' },
  ]

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={arSA}>
      <Stack spacing={2.5}>
        <Box
          sx={{
            display: 'grid',
            gap: 1.5,
            gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(5, 1fr)' },
          }}
        >
          {statCards.map((card) => {
            const selected = filter === card.key
            return (
              <Paper
                key={card.key}
                onClick={() => setFilter(card.key)}
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  border: '1px solid',
                  borderColor: selected ? card.color : 'divider',
                  boxShadow: selected ? `0 0 0 2px ${card.color}22` : undefined,
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {card.label}
                </Typography>
                <Typography variant="h4" sx={{ color: card.color, mt: 0.5 }}>
                  {card.value}
                </Typography>
              </Paper>
            )
          })}
        </Box>

        <Paper sx={{ p: 2 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
            <TextField
              placeholder="بحث بالاسم أو الهاتف"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ flex: 1, minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <FormControl sx={{ minWidth: 180 }}>
              <InputLabel>الحالة</InputLabel>
              <Select
                label="الحالة"
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterType)}
              >
                {Object.entries(FILTER_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value}>
                    {label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              onClick={runManualSubscriptionCheck}
              disabled={manualCheckLoading}
              startIcon={<WarningIcon />}
            >
              {manualCheckLoading ? 'جاري الفحص...' : 'فحص الاشتراكات'}
            </Button>
          </Stack>
        </Paper>

        {selectedIds.length > 0 && (
          <Paper sx={{ p: 1.5, bgcolor: 'rgba(15,118,110,0.06)' }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
              <Typography sx={{ flex: 1, fontWeight: 600 }}>
                تم تحديد {selectedIds.length} تاجر
              </Typography>
              <Button size="small" variant="contained" onClick={openBulkDateDialog}>
                تحديد تاريخ للجميع
              </Button>
              <Button
                size="small"
                color="warning"
                onClick={async () => {
                  try {
                    const targets = merchants.filter((m) => selectedIds.includes(m.id))
                    for (const merchant of targets) {
                      await applyUnlimited(merchant)
                    }
                    notify(`تم جعل ${targets.length} تاجر غير محدود`, 'success')
                    setSelectedIds([])
                  } catch {
                    notify('حدث خطأ أثناء التحديث', 'error')
                  }
                }}
              >
                جعلهم غير محدود
              </Button>
              <Button size="small" onClick={() => setSelectedIds([])}>
                إلغاء التحديد
              </Button>
            </Stack>
          </Paper>
        )}

        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={pageRows.length > 0 && selectedOnPage.length === pageRows.length}
                    indeterminate={selectedOnPage.length > 0 && selectedOnPage.length < pageRows.length}
                    onChange={(e) => toggleAllPage(e.target.checked)}
                  />
                </TableCell>
                <TableCell>التاجر</TableCell>
                <TableCell>الهاتف</TableCell>
                <TableCell>الحالة</TableCell>
                <TableCell>تاريخ الانتهاء</TableCell>
                <TableCell>المتبقي</TableCell>
                <TableCell align="center">إجراء</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {pageRows.map((merchant) => {
                const days = getDaysRemaining(merchant.subscriptionEndDate)
                return (
                  <TableRow key={merchant.id} hover selected={selectedIds.includes(merchant.id)}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedIds.includes(merchant.id)}
                        onChange={() =>
                          setSelectedIds((prev) =>
                            prev.includes(merchant.id)
                              ? prev.filter((id) => id !== merchant.id)
                              : [...prev, merchant.id],
                          )
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{merchant.name || 'بدون اسم'}</Typography>
                      {merchant.isSuperSeller && (
                        <Chip label="سوبر" size="small" color="primary" sx={{ mt: 0.5 }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ direction: 'ltr', textAlign: 'right' }}>
                      {merchant.phone || '—'}
                    </TableCell>
                    <TableCell>{statusChip(merchant)}</TableCell>
                    <TableCell>{formatDateAr(merchant.subscriptionEndDate)}</TableCell>
                    <TableCell>
                      {days == null ? (
                        <Typography color="text.secondary">غير محدود</Typography>
                      ) : days < 0 ? (
                        <Typography color="error.main">منتهي منذ {Math.abs(days)} يوم</Typography>
                      ) : (
                        <Typography color={days <= 7 ? 'warning.main' : 'success.main'}>
                          {days} يوم
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="تحديد تاريخ الاشتراك">
                        <IconButton color="primary" onClick={() => openSubscriptionDialog(merchant)}>
                          <EventIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                )
              })}
              {pageRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                    <Typography color="text.secondary">لا يوجد تجار مطابقون للبحث أو الفلتر</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={filteredMerchants.length}
            page={page}
            onPageChange={(_, next) => setPage(next)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10))
              setPage(0)
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="عدد الصفوف"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} من ${count}`}
          />
        </TableContainer>

        <Dialog open={subscriptionDialog} onClose={() => setSubscriptionDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {bulkMode ? `تحديد تاريخ لـ ${selectedIds.length} تاجر` : 'تحديد تاريخ انتهاء الاشتراك'}
            {selectedMerchant && (
              <Typography variant="body2" color="text.secondary">
                التاجر: {selectedMerchant.name}
              </Typography>
            )}
          </DialogTitle>
          <DialogContent>
            <Alert severity="info" sx={{ my: 2 }}>
              سيتم إرسال تذكير قبل يوم من الانتهاء، وعند الانتهاء يتوقف إرسال العروض.
            </Alert>
            <DatePicker
              label="تاريخ انتهاء الاشتراك"
              value={selectedDate}
              onChange={(value) => setSelectedDate(value)}
              minDate={new Date()}
              slotProps={{ textField: { fullWidth: true } }}
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={removeSubscription} color="warning" sx={{ mr: 'auto' }}>
              غير محدود
            </Button>
            <Button onClick={() => setSubscriptionDialog(false)}>إلغاء</Button>
            <Button variant="contained" onClick={saveSubscription} disabled={!selectedDate}>
              حفظ
            </Button>
          </DialogActions>
        </Dialog>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setSnackbar({ ...snackbar, open: false })}
            severity={snackbar.severity}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Stack>
    </LocalizationProvider>
  )
}
